<template>
  <div class="two-template">
    <!-- Название проекта -->
    <div class="two-template__header">
      <BaseTypography with-pre-line type="h2">
        {{ data.title }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.description }}
      </BaseTypography>
      <TemplateLandingButtonView
        v-if="buttons.header"
        :button="buttons.header"
      />
    </div>

    <div class="two-template__main-section">
      <div class="two-template__main-image">
        <TemplateMediaView
          v-if="data.images.mainImage"
          :src="data.images.mainImage"
          :aspect-width="736"
          :aspect-height="560"
        />
      </div>
      <div class="two-template__side-images">
        <TemplateMediaView
          v-if="data.images.sideTop"
          :src="data.images.sideTop"
          :aspect-width="272"
          :aspect-height="272"
        />
        <TemplateMediaView
          v-if="data.images.sideBottom"
          :src="data.images.sideBottom"
          :aspect-width="272"
          :aspect-height="272"
        />
      </div>
    </div>

    <!-- Два заголовка H2 -->
    <div class="two-template__dual-headers">
      <div class="two-template__header-left">
        <BaseTypography with-pre-line type="h2">
          {{ data.leftSectionTitle }}
        </BaseTypography>
        <BaseTypography with-pre-line type="text" weight="400">
          {{ data.leftSectionDescription }}
        </BaseTypography>
        <TemplateLandingButtonView
          v-if="buttons.leftHeader"
          full-width
          :button="buttons.leftHeader"
        />
      </div>
      <div class="two-template__header-right">
        <BaseTypography with-pre-line type="h2">
          {{ data.rightSectionTitle }}
        </BaseTypography>
        <BaseTypography with-pre-line type="text" weight="400">
          {{ data.rightSectionDescription }}
        </BaseTypography>
        <TemplateLandingButtonView
          v-if="buttons.rightHeader"
          full-width
          :button="buttons.rightHeader"
        />
      </div>
    </div>

    <!-- Три изображения в ряд -->
    <div class="two-template__triple-images">
      <div class="two-template__triple-image">
        <TemplateMediaView
          v-if="data.images.triple1"
          :src="data.images.triple1"
          :aspect-width="419"
          :aspect-height="280"
        />
      </div>
      <div class="two-template__triple-image">
        <TemplateMediaView
          v-if="data.images.triple2"
          :src="data.images.triple2"
          :aspect-width="419"
          :aspect-height="280"
        />
      </div>
      <div class="two-template__triple-image">
        <TemplateMediaView
          v-if="data.images.triple3"
          :src="data.images.triple3"
          :aspect-width="419"
          :aspect-height="280"
        />
      </div>
    </div>

    <div class="two-template__additional-section">
      <BaseTypography with-pre-line type="h2">
        {{ data.bottomSectionTitle }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.bottomSectionDescription }}
      </BaseTypography>
      <TemplateLandingButtonView
        v-if="buttons.additional"
        :button="buttons.additional"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ITwoTemplateData, TemplateData } from '~/entities/projects/model/templates-interface'
import { computed } from 'vue'
import { useTemplateViewButtons } from '~/entities/projects/model/useTemplateViewButtons'
import TemplateLandingButtonView from '~/entities/projects/ui/templates/TemplateLandingButtonView.vue'
import TemplateMediaView from '~/entities/projects/ui/templates/TemplateMediaView.vue'
import { BaseTypography } from '~/shared/ui'

interface Props {
  templateData?: TemplateData | null
}

const props = withDefaults(defineProps<Props>(), {
  templateData: null,
})

const data = computed(() => {
  if (props.templateData) {
    return props.templateData as ITwoTemplateData
  }

  return {
    title: 'Как заполнить этот шаблон',
    description: 'Заголовок — напишите короткую фразу из 3–7 слов. Она должна быть простой и понятной. Заголовок должен передавать позитивное настроение. Например: про новый день, вдохновение, движение вперёд, радость, развитие. Не используйте сложные слова и длинные предложения.',
    leftSectionTitle: 'Подзаголовок',
    leftSectionDescription: 'Напишите текст объёмом до 1000 символов. Лучше разделить его на 2–3 абзаца, чтобы текст было удобно читать. Один абзац — это 2–4 предложения.\n\nПишите простым языком. Представьте, что вы объясняете мысль другу. Текст должен быть лёгким, понятным и нейтральным. Он не должен быть привязан к конкретному продукту, компании или услуге. Это универсальный текст, который используется как пример для шаблона.',
    rightSectionTitle: 'Подзаголовок',
    rightSectionDescription: 'Можно писать о вдохновении, новых возможностях, развитии, спокойствии, ценности момента, движении вперёд или простых радостях жизни.\n\nСтарайтесь: не использовать сложные профессиональные термины, не добавлять агрессивную рекламу, не писать слишком длинные предложения, не перегружать текст цифрами и фактами.\n\nВажно, чтобы текст выглядел аккуратно в блоке и легко воспринимался при быстром чтении.',
    bottomSectionTitle: 'Подзаголовок',
    bottomSectionDescription: 'Старайтесь не перегружать предложения и не делать их слишком длинными. Лучше разбивать текст на небольшие абзацы по несколько предложений, чтобы его было удобно читать даже при быстром просмотре страницы. Избегайте негативных формулировок, громких обещаний и излишнего пафоса. Текст должен выглядеть естественно и вызывать ощущение спокойствия и легкости. Если после прочтения создаётся ощущение ясности и простоты, значит текст подходит для шаблона. Главное — чтобы он был понятен любому человеку независимо от возраста и опыта.',
    images: {
      mainImage: '/projects/project-template-3-bg-1.webp',
      sideTop: '/projects/project-template-3-bg-2.webp',
      sideBottom: '/projects/project-template-3-bg-3.webp',
      triple1: '/projects/project-template-3-bg-4.webp',
      triple2: '/projects/project-template-3-bg-5.webp',
      triple3: '/projects/project-template-3-bg-6.webp',
    },
  }
})

const buttons = useTemplateViewButtons(() => props.templateData)
</script>

<style lang="scss" scoped>
.two-template {
  --main-image-max-width: 100%;
  --side-image-max-width: 100%;
  --triple-image-max-width: 100%;

  display: grid;
  gap: rem(60px);
  grid-template-columns: 1fr;

  @include bp-media('xs', 'sm', 'md') {
    gap: rem(40px);
  }

  > * {
    min-width: 0;
  }

  &__header {
    display: grid;
    gap: rem(16px);

    > * {
      min-width: 0;
    }
  }

  &__main-section {
    display: grid;
    grid-template-columns: 3fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__main-image {
    overflow: hidden;
    border-radius: rem(16px);
    display: grid;
  }

  &__side-images {
    display: grid;
    gap: rem(16px);

    @include bp-media('xs', 'sm') {
      height: 100%;
      grid-template-columns: 1fr 1fr;
    }
  }

  &__triple-image {
    overflow: hidden;
    border-radius: rem(16px);
    display: grid;
  }

  &__dual-headers {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(32px);

    > * {
      min-width: 0;
    }

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__header-left,
  &__header-right {
    display: grid;
    gap: rem(16px);

    > * {
      min-width: 0;
    }
  }

  &__triple-images {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: rem(28px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__additional-section {
    display: grid;
    gap: rem(16px);

    > * {
      min-width: 0;
    }
  }

}
</style>
